import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { IAiTool, IAiToolSuggestion } from 'src/app/models/ai-tool.model';
import { Subscription, finalize } from 'rxjs';
import { IConfirmData } from 'src/app/models/confirm-data';
import { faCommentDots, faLink, faTag } from '@fortawesome/free-solid-svg-icons';
import { faWordpress } from '@fortawesome/free-brands-svg-icons';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { ToastService } from 'src/app/services/toast.service';
import { AiToolService } from 'src/app/services/aiTool.service';
import { HelperService } from 'src/app/services/helper.service';
import { EToolStatus } from 'src/app/enum/ai-tool.enum';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-ai-tool-review-card',
  templateUrl: './ai-tool-review-card.component.html',
  styleUrl: './ai-tool-review-card.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AiToolCardComponent {
  @ViewChild('commentContainer') private commentContainer!: ElementRef;
  @Input() tool: IAiTool;
  @Output() toolCardRemoved = new EventEmitter<IAiTool>();
  @Output() onEditTool = new EventEmitter<IAiTool>();
  @Output() refreshAiToolsList = new EventEmitter<void>();
  public badgeIcon = faTag;

  public newComment = '';
  public showComments = false;
  public confirmDialog = false;
  public confirmPublishDialog = false;
  private subscription = new Subscription();
  public modalData = {} as IConfirmData;
  public commentIcon = faCommentDots;
  public faLink = faLink;
  public faWordpress = faWordpress;
  public EToolStatus = EToolStatus;
  public showMore = false;
  public toolImage: string = null;
  public imageLoading = false;

  constructor(
    private router: Router,
    public authService: AuthService,
    private toastService: ToastService,
    private aiToolService: AiToolService,
    public helperService: HelperService,
  ) { }

  ngOnInit() {
    if (this.tool.image) {
      this.getToolImage(this.tool.image as string);
    }
  }

  public isMobile(): boolean {
    return window.innerWidth < 1023;
  }

  public onEditClick(): void {
    this.onEditTool.emit(this.tool);
  }

  public onPublishClick(): void {
    this.confirmPublishDialog = true;
    this.modalData.headline = `Publish to WordPress`;
    this.modalData.text = `Are you sure you want to publish this AI tool review to WordPress?`;
    this.modalData.image = 'wordpress';
    this.modalData.isLoading = false;
  }

  public onPublishConfirmation(): void {
    this.modalData.headline = 'Publishing to WordPress';
    this.modalData.text = 'AI Tool review is being published to WordPress, please wait...';
    this.modalData.image = 'wordpress';
    this.modalData.isLoading = true;

    this.subscription.add(
      this.aiToolService
        .publishToWordPress(this.tool._id)
        .pipe(
          finalize(() => {
            this.confirmPublishDialog = false;
            this.modalData.isLoading = false;
          }),
        )
        .subscribe({
          next: (response) => {
            this.toastService.success(response?.MESSAGE || 'Successfully published the report to WordPress');
            this.refreshAiToolsList.emit();
          },
          error: (err) => {
            this.toastService.error(err.error?.MESSAGE || 'Failed to publish the report');
          },
        }),
    );
  }

  public closeToolCard(): void {
    this.subscription.add(
      this.aiToolService.closeToolCard(this.tool._id).subscribe({
        next: () => {
          this.toolCardRemoved.emit(this.tool as IAiTool);
          this.toastService.success('Tool card was closed successfully');
        },
        error: () => {
          this.toastService.error('Something went wrong');
        },
      }),
    );
  }

  public onKeyUp(event: KeyboardEvent, isAdvantageField = false): void {
    if (event.key !== 'Enter') return;
    this.onCreateComment();
  }

  public voteTool(tool: IAiTool, voteType: 'up' | 'down'): void {
    const employeeId = this.authService.getCurrentEmployee()?._id;

    const userHasVoted = (this.tool as IAiTool).hasVoted.includes(employeeId);
    if (userHasVoted) {
      this.toastService.error('You have already voted');
      return;
    }
    const newCount = voteType === 'up' ? tool.voteCount + 1 : tool.voteCount - 1;
    this.subscription.add(
      this.aiToolService.voteTool(this.tool._id, newCount).subscribe({
        next: () => {
          (this.tool as IAiTool).voteCount = newCount;
          this.toastService.success('Tool was successfully voted');
        },
        error: () => {
          this.toastService.error('Failed to vote on tool');
        },
      }),
    );
  }

  public deleteToolCard(): void {
    this.subscription.add(
      this.aiToolService.deleteToolReview(this.tool._id).subscribe({
        next: () => {
          this.toolCardRemoved.emit(this.tool as IAiTool);
          this.toastService.success('Tool was deleted successfully');
        },
        error: () => {
          this.toastService.error('Something went wrong');
        },
      }),
    );
  }

  public toggleComments(): void {
    this.showComments = !this.showComments;
    if (this.showComments) {
      setTimeout(() => {
        this.scrollToBottom();
      });
    }
  }

  private scrollToBottom(): void {
    const container = this.commentContainer.nativeElement;
    container.scrollTop = container.scrollHeight;
  }

  public onDeleteModalConfirmation(): void {
    if (this.tool.status === EToolStatus.CLOSED) {
      this.deleteToolCard();
    } else {
      this.closeToolCard();
    }
  }

  public onCreateComment(): void {
    this.subscription.add(
      this.aiToolService.createComment(this.tool._id, this.newComment).subscribe({
        next: (res) => {
          (this.tool as IAiTool).comments = res.DATA.comments;
          this.toastService.success('Comment added successfully');
        },
        error: () => {
          this.toastService.error('Something went wrong');
        },
        complete: () => {
          this.newComment = '';
        },
      }),
    );
  }

  public deleteComment(commentId: string): void {
    this.subscription.add(
      this.aiToolService.deleteComment(this.tool._id, commentId).subscribe({
        next: () => {
          this.tool.comments = this.tool.comments.filter((comment) => comment._id !== commentId);
          this.toastService.success('Comment deleted successfully');
        },
        error: () => {
          this.toastService.error('Something went wrong');
        },
      }),
    );
  }

  public createRemoveModal(): void {
    if (this.tool.status === EToolStatus.CLOSED) {
      this.confirmDialog = true;
      this.modalData.headline = `Delete Tool Card?`;
      this.modalData.text = `Are you sure yout want to delete this card permanently?`;
      this.modalData.image = 'delete';
    } else {
      this.confirmDialog = true;
      this.modalData.headline = `Close Tool Card`;
      this.modalData.text = `Are you sure you want to set this card to closed?`;
      this.modalData.image = 'feedback';
    }
  }

  public showCloseButton(): boolean {
    return this.authService.getCurrentEmployee().isAdmin && !this.router.url.includes('/dashboard');
  }

  // DEPRECATED - not used
  public getImageUrl(): string | undefined {
    if (this.tool && this.tool.image && typeof this.tool.image === 'string') {
      return `${environment.baseUrl}/tool/image/${this.tool.image}`;
    }
    return undefined;
  }

  public getToolImage(imageId: string): void {
    this.subscription.add(
      this.aiToolService.getToolImage(imageId).subscribe({
        next: (res) => {
          this.toolImage = URL.createObjectURL(res);
          this.imageLoading = false;
        },
        error: (err) => {
          this.toastService.error(err.error.MESSAGE);
        },
      }),
    );
  }

  ngOnDestroy(): void {
    if (this.toolImage) {
      URL.revokeObjectURL(this.toolImage);
    }
    this.subscription.unsubscribe();
  }
}
